import {
  AdminIcon,
  CustomerListIcon,
  DashboardIcon,
  DocumentsIcons,
  ExpenseTrackerIcon,
  HelpIcon,
  IntegrationsIcon,
  LeadManagementIcon,
  OrgSettingsIcon,
  ReportsIcon,
  TeamManagementIcon
} from '@images';

import {
  ADMIN,
  BRANCHES,
  DOCS,
  EXPENSE_TRACKER,
  HELP,
  LEAD_MANAGEMENT,
  ORG_DASHBOARD,
  ORG_SETTINGS,
  REPORTS,
  SUBSCRIPTIONS,
  TALLY_INTEGRATIONS,
  TEAM_MANAGEMENT
} from '@constants/routes';
import { WebhooksEvents } from '@features/docs/constants';

import { FEATURE_FLAGS } from './feature';
import UserPermissionTypes, {
  TEAM_MANAGEMENT_PERMISSIONS
} from './permissions';
import {
  DASHBOARD_ITEM,
  EVENTS_ITEM,
  FORMS_ITEM,
  GROUPS_ITEM,
  MEMBERS_ITEM,
  ORG_SETTINGS_ITEM,
  PAYMENT_DROPOFFS_ITEM,
  QUICK_COLLECT_ITEM,
  REPORT_PERMISSIONS,
  REPORTS_ITEM,
  SUBSCRIPTIONS_ITEM,
  TRANSACTIONS_ITEM
} from './sideMenuItems';

export const groupsTabPathPattern =
  /^\/groups(\/add|\/grp_[A-Za-z0-9]*(?:\/(?:edit|add-members))?)?$/;

const INDIVIDUAL_MENU_ITEMS = [
  DASHBOARD_ITEM,
  MEMBERS_ITEM,
  GROUPS_ITEM,
  QUICK_COLLECT_ITEM,
  TRANSACTIONS_ITEM,
  PAYMENT_DROPOFFS_ITEM,
  EVENTS_ITEM,
  FORMS_ITEM
];

const PROFILE_ITEM_PATHS = [
  REPORTS,
  ORG_SETTINGS,
  ORG_DASHBOARD,
  EXPENSE_TRACKER.EXPENSE_DASHBOARD,
  EXPENSE_TRACKER.EXPENSE_LIST,
  EXPENSE_TRACKER.REPORTS,
  TEAM_MANAGEMENT.TEAM_MANAGEMENT,
  SUBSCRIPTIONS.PLAN_DETAILS,
  DOCS.OVERVIEW,
  DOCS.API_REFERENCE,
  DOCS.API_KEYS,
  DOCS.WEBHOOKS,
  DOCS.SDK,
  DOCS.WEBHOOK_REFERENCE,
  HELP,
  LEAD_MANAGEMENT.HOME,
  TALLY_INTEGRATIONS.TALLY_INTEGRATIONS
];

const DOCS_NAV_ITEM = {
  id: 'docs',
  Icon: DocumentsIcons,
  label: 'Docs',
  route: DOCS.OVERVIEW,
  Permissions: [UserPermissionTypes.INTEGRATION_EDIT],
  children: [
    { id: 'overview', label: 'Overview', route: DOCS.OVERVIEW },
    {
      id: 'api',
      label: 'API',
      route: DOCS.API_KEYS,
      children: [
        { id: 'api-keys', label: 'API Keys', route: DOCS.API_KEYS },
        {
          id: 'api-reference',
          label: 'API Reference',
          route: DOCS.API_REFERENCE
        }
      ]
    },
    {
      id: 'sdk',
      label: 'SDK',
      route: DOCS.SDK
    },
    {
      id: 'webhooks',
      label: 'Webhooks',
      route: DOCS.WEBHOOKS,
      children: [
        {
          id: 'webhook-configuration',
          label: 'Configuration',
          route: DOCS.WEBHOOKS
        },
        ...WebhooksEvents.map((event) => ({
          id: event.id,
          label: event.label,
          route: event.route
        }))
      ]
    }
  ]
};

const INDIVIDUAL_PROFILE_ITEMS = [
  [REPORTS_ITEM],
  [
    ORG_SETTINGS_ITEM,
    SUBSCRIPTIONS_ITEM
    // {
    //   id: 'help',
    //   Icon: HelpIcon,
    //   label: 'Help',
    //   route: HELP
    // }
  ],
  [DOCS_NAV_ITEM]
];

// Here
const ORG_PROFILE_SECTIONS = [
  // Section 1
  [
    {
      id: 'org-dashboard',
      Icon: DashboardIcon,
      label: 'Organization Dashboard',
      route: ORG_DASHBOARD,
      permissions: [UserPermissionTypes.ORG_DASHBOARD_VIEW],
      icon: 'dashboard'
    },
    REPORTS_ITEM,
    {
      id: 'expence-tracker',
      Icon: ExpenseTrackerIcon,
      label: 'Expense Tracker',
      route: EXPENSE_TRACKER.EXPENSE_DASHBOARD,
      permissions: [UserPermissionTypes.EXPENSE_VIEW],
      featureFlag: FEATURE_FLAGS.EXPENSE_TRACKER,
      children: [
        {
          id: 'expense-tracker-dashboard',
          label: 'Expense Dashboard',
          route: EXPENSE_TRACKER.EXPENSE_DASHBOARD
        },
        {
          id: 'expense-tracker-list',
          label: 'Expense List',
          route: EXPENSE_TRACKER.EXPENSE_LIST
        },
        {
          id: 'expense-tracker-reports',
          label: 'Reports',
          route: EXPENSE_TRACKER.REPORTS
        }
      ]
    },
    {
      id: 'lead-management',
      Icon: LeadManagementIcon,
      label: 'Lead Management',
      route: LEAD_MANAGEMENT.LEAD_DASHBOARD,
      permissions: [UserPermissionTypes.LEAD_MANAGEMENT_VIEW],
      featureFlag: FEATURE_FLAGS.LEAD_MANAGEMENT,
      children: [
        {
          id: 'lead-management-dashboard',
          label: 'Dashboard',
          route: LEAD_MANAGEMENT.LEAD_DASHBOARD
        },
        {
          id: 'lead-management-list',
          label: 'Leads',
          route: LEAD_MANAGEMENT.LEAD_LIST,
          additionalChildRoutes: [
            LEAD_MANAGEMENT.LEAD_ADD,
            LEAD_MANAGEMENT.LEAD_DETAILS,
            LEAD_MANAGEMENT.LEAD_EDIT
          ]
        },
        {
          id: 'source-analytics',
          label: 'Sources',
          route: LEAD_MANAGEMENT.LEAD_SOURCES,
          additionalChildRoutes: [LEAD_MANAGEMENT.LEAD_AD_SOURCES]
        },
        {
          id: 'agents',
          label: 'Agents',
          route: LEAD_MANAGEMENT.LEAD_AGENTS
        },
        {
          id: 'lead-management-activities',
          label: 'My Activities',
          route: LEAD_MANAGEMENT.LEAD_ACTIVITIES
        },
        {
          id: 'lead-management-reports',
          label: 'Reports',
          route: LEAD_MANAGEMENT.LEAD_REPORTS,
          permissions: [UserPermissionTypes.LEAD_REPORT_GENERATE]
        },
        {
          id: 'lead-management-settings',
          label: 'Settings',
          route: LEAD_MANAGEMENT.LEAD_SETTINGS,
          permissions: [UserPermissionTypes.LEAD_MANAGEMENT_CONFIG_VIEW]
        }
      ]
    },
    {
      id: 'tally-integration',
      Icon: IntegrationsIcon,
      label: 'Tally Integration',
      route: TALLY_INTEGRATIONS.TALLY_INTEGRATIONS,
      permissions: [UserPermissionTypes.INTEGRATION_EDIT],
      featureFlag: FEATURE_FLAGS.TALLY_INTEGRATION
    }
  ],
  // Section 2
  [
    ORG_SETTINGS_ITEM,
    {
      id: 'team-management',
      Icon: TeamManagementIcon,
      label: 'Team Management',
      route: TEAM_MANAGEMENT.TEAM_MANAGEMENT,
      permissions: TEAM_MANAGEMENT_PERMISSIONS
    },
    SUBSCRIPTIONS_ITEM
    // {
    //   id: 'help',
    //   Icon: HelpIcon,
    //   label: 'Help',
    //   route: HELP
    // }
  ],
  // Section 3
  [DOCS_NAV_ITEM]
];

const ORG_MENU_ITEMS = [
  DASHBOARD_ITEM,
  MEMBERS_ITEM,
  GROUPS_ITEM,
  QUICK_COLLECT_ITEM,
  TRANSACTIONS_ITEM,
  {
    id: 'branch-reports',
    Icon: ReportsIcon,
    label: 'Branch Reports',
    route: BRANCHES.REPORTS,
    permissions: REPORT_PERMISSIONS
  },
  PAYMENT_DROPOFFS_ITEM,
  EVENTS_ITEM,
  FORMS_ITEM,
  {
    id: 'branches',
    Icon: OrgSettingsIcon,
    label: 'Branch Settings',
    route: BRANCHES.EDIT_BRANCH,
    permissions: [UserPermissionTypes.BRANCH_EDIT],
    additionalChildRoutes: [BRANCHES.MANAGE_BANK_ACCOUNT]
  }
];

const ADMIN_MENU_ITEMS = [
  {
    id: 'kyc-documents',
    Icon: AdminIcon,
    label: 'KYC Documents',
    route: ADMIN.KYC_DOCUMENTS
  },
  {
    id: 'customers-list',
    Icon: CustomerListIcon,
    label: 'Customers List',
    route: ADMIN.CUSTOMERS_LIST,
    permissions: [UserPermissionTypes.CUSTOMER_LIST_VIEW]
  }
];

const HELP_ITEM = {
  id: 'help',
  Icon: HelpIcon,
  label: 'Help',
  route: HELP
};

export {
  ADMIN_MENU_ITEMS,
  HELP_ITEM,
  INDIVIDUAL_MENU_ITEMS,
  INDIVIDUAL_PROFILE_ITEMS,
  ORG_MENU_ITEMS,
  ORG_PROFILE_SECTIONS,
  PROFILE_ITEM_PATHS
};
